import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { CustomDropdown, createAssigneeOptions, getAssigneeIcon, outOfOfficeForAssignee, OutOfOfficeMark } from '@medprompt/shared';

const BulkAssignDocumentsModal = ({ isOpen, onClose, onConfirm, documents, activeUsers, teams, loading, getAssigneeName }) => {
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [assigneeDropdownOpen, setAssigneeDropdownOpen] = useState(false);

  // Create assignee options with teams and users
  const assigneeOptions = createAssigneeOptions(activeUsers, teams);

  useEffect(() => {
    if (isOpen) {
      // Default to "Unassigned" option (first option)
      setSelectedAssignee(createAssigneeOptions(activeUsers, teams)[0]);
      setAssigneeDropdownOpen(false);
    } else {
      setSelectedAssignee(null);
      setAssigneeDropdownOpen(false);
    }
  }, [isOpen, activeUsers, teams]);

  const handleAssigneeSelect = (assignee) => {
    setSelectedAssignee(assignee);
    setAssigneeDropdownOpen(false);
  };

  const handleConfirm = () => {
    if (selectedAssignee) {
      // Send '*' for unassigned (null userId), otherwise send the actual userId
      const assigneeIdToSend = selectedAssignee.userId === null ? '*' : selectedAssignee.userId;
      onConfirm(assigneeIdToSend);
    }
  };

  if (!isOpen || !documents || documents.length === 0) return null;

  const selectedIcon = selectedAssignee ? getAssigneeIcon(selectedAssignee.userId, activeUsers, teams) : null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2>Assign Documents</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="modal-content">
          <p>Assign {documents.length} document{documents.length > 1 ? 's' : ''} to:</p>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#333' }}>Assignee:</label>
            <CustomDropdown
              searchable
              searchPlaceholder="Search assignees..."
              selected={selectedAssignee?.display || 'Unassigned'}
              selectedIcon={selectedIcon}
              selectedOutOfOffice={outOfOfficeForAssignee(selectedAssignee?.userId, activeUsers)}
              options={assigneeOptions}
              isOpen={assigneeDropdownOpen}
              toggleDropdown={() => setAssigneeDropdownOpen(!assigneeDropdownOpen)}
              onSelect={handleAssigneeSelect}
              keyExtractor={(option) => option.id}
              maxHeight="400px"
            />
          </div>

          <div className="bulk-document-list">
            {documents.slice(0, 5).map(document => (
              <div key={document.documentId} className="bulk-document-item">
                <div className="bulk-document-name">{document.documentTitle || "Untitled"}</div>
                <div className="bulk-document-assignee assignee-with-availability">
                  Current: {getAssigneeName(document.assigneeId)}
                  <OutOfOfficeMark outOfOffice={outOfOfficeForAssignee(document.assigneeId, activeUsers)} />
                </div>
              </div>
            ))}
            {documents.length > 5 && (
              <div className="bulk-document-more">
                ... and {documents.length - 5} more document{documents.length - 5 > 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
        <div className="modal-actions">
          <button className="cancel-button" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="regenerate-confirm-button"
            onClick={handleConfirm}
            disabled={loading || !selectedAssignee}
          >
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              'Assign All'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignDocumentsModal;
